import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Menu, Modal, Space, Tag, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import { DownOutlined } from '@ant-design/icons';
import FilterDropdown from './FilterDropDown';
import IconSort from '../../../../assets/icon_sort.svg';
import { selectedAlertFilters } from '../../../../redux/actions/ns/onDemandScreening';
import { sendToCMAlerts } from '../../../../redux/actions/ns/investigation';
import {
  StyledAlertListFilters,
  StyledButtonGroup,
  StyledCSVLink,
  StyledSearch,
  StyledTag,
  StyledTagDiv
} from './styled';
import moment from 'moment';
import { generateCsvConfigs } from '../../../common/utils';
import { normalizeAlertScore } from '../../../../util/normalization';
import { convertISOToDateTimeWithoutSeconds } from '../../../../util/timezone';
import { DDMMYYYYHHMMTimeFormatForFileDownload } from '../../../../util/GlobalConstant';

const { confirm } = Modal;

export const ALERT_LEVEL_OPTIONS = [
  {
    label: 'All',
    value: ''
  },
  {
    label: 'High',
    value: 'High'
  },
  {
    label: 'Good',
    value: 'Good'
  },
  {
    label: 'Low',
    value: 'Low'
  }
];

const AlertListFilters = ({
  jobId,
  dispatch,
  filters,
  selectedIds,
  selectedAlerts,
  customerType,
  widgetConfig,
  onClearJobId
}) => {
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setSearchValue(filters?.searchValue);
  }, [filters]);

  const isSentCount = useMemo(() => {
    return selectedAlerts.filter(({ isSentToCm }) => isSentToCm);
  }, [selectedAlerts]);

  const sendToCM = () => {
    let msg = '';

    if (isSentCount.length > 0) {
      msg = `${selectedIds.length} selected alerts, ${
        selectedIds.length - isSentCount.length
      } will be sent to CM as alert and ${isSentCount.length} ${
        isSentCount.length > 1 ? 'are' : 'is'
      } already sent, so wont be sent again.`;
    } else {
      msg = `Are you sure you want to generate an alert in CM?`;
    }
    confirm({
      title: `Send to CM`,
      content: msg,
      centered: true,
      okText: 'Confirm',
      width: 500,
      okButtonProps: {
        style: {
          backgroundColor: '#2B73E0'
        }
      },
      onOk: () => {
        dispatch(
          sendToCMAlerts({
            ids: selectedIds.filter(
              (a) => isSentCount.findIndex((f) => f.alertId === a) === -1
            ),
            isFromAlertList: true
          })
        );
      }
    });
  };

  const csvData = useMemo(() => {
    return selectedAlerts.map((alert) => ({
      ...alert,
      alertScore: `${normalizeAlertScore(alert.alertScore || 0).toFixed(2)}%`,
      alertDate: convertISOToDateTimeWithoutSeconds(alert.alertDate),
      alertSourceId: alert.alertSourceId === '28' ? 'Bulk' : 'Single',
      isSentToCm: alert.isSentToCm ? 'Yes' : 'No',
      totalHits:
        (alert.hitsSummary?.High || 0) +
        (alert.hitsSummary?.Good || alert.hitsSummary?.Medium || 0) +
        (alert.hitsSummary?.Low || 0),
      highHits: alert.hitsSummary?.High || 0,
      mediumHits: alert.hitsSummary?.Good || alert.hitsSummary?.Medium || 0,
      lowHits: alert.hitsSummary?.Low || 0
    }));
  }, [selectedAlerts]);

  const csvHeaders = useMemo(() => {
    let headers = generateCsvConfigs({
      settings: widgetConfig
    });

    headers = headers
      ?.filter(({ key }) => key !== 'listDetails')
      .map((header) => {
        if (header?.key === 'isSentToCm') {
          return {
            ...header,
            label: 'Sent to CM'
          };
        } else if (header?.key === 'alertLevel') {
          return {
            ...header,
            label: 'Alert Level'
          };
        } else {
          return header;
        }
      });

    headers?.map((header, index) => {
      //Split the hits values to separate columns
      if (header?.key === 'hitsSummary') {
        headers.splice(index, 1, {
          label: 'Total Hits',
          name: 'totalHits',
          key: 'totalHits'
        });
        headers.splice(index + 1, 0, {
          label: 'Hits (High)',
          name: 'highHits',
          key: 'highHits'
        });
        headers.splice(index + 2, 0, {
          label: 'Hits (Medium)',
          name: 'mediumHits',
          key: 'mediumHits'
        });
        headers.splice(index + 3, 0, {
          label: 'Hits (Low)',
          name: 'lowHits',
          key: 'lowHits'
        });
      } else if (header?.key === 'alertLevel') {
        headers.splice(index + 1, 0, {
          label: 'Alert Score',
          name: 'alertScore',
          key: 'alertScore'
        });
      }
    });

    return headers;
  }, [widgetConfig]);

  const takeActionMenu = (
    <Menu>
      <Menu.Item key="1">
        <StyledCSVLink
          data={csvData}
          headers={csvHeaders}
          filename={`Alert_Listing_Page__${moment().format(
            DDMMYYYYHHMMTimeFormatForFileDownload
          )}.csv`}>
          Download selected alerts
        </StyledCSVLink>
      </Menu.Item>
      <Menu.Item
        disabled={isSentCount.length === selectedIds.length}
        key="2"
        onClick={() => sendToCM()}>
        Send to CM
      </Menu.Item>
    </Menu>
  );

  const onFilterChange = (value) => {
    dispatch(selectedAlertFilters({ ...filters, ...value }));
  };

  const onSearchButtonClick = (value) => {
    // Check previous search value same as current value. If same then omit the filter update.
    if (value !== filters?.searchValue) {
      dispatch(selectedAlertFilters({ ...filters, searchValue: value }));
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '15px'
      }}>
      <StyledButtonGroup size="middle">
        {React.Children.toArray(
          ALERT_LEVEL_OPTIONS.map(({ label, value }) => (
            <Button
              className={filters?.alertLevel === value ? 'selected' : ''}
              onClick={() => onFilterChange({ alertLevel: value })}>
              {label}
            </Button>
          ))
        )}
      </StyledButtonGroup>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {jobId && (
          <StyledTagDiv>
            <label>Bulk Run ID</label>
            <StyledTag
              closable
              bordered={false}
              onClose={onClearJobId}
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              {jobId}
            </StyledTag>
          </StyledTagDiv>
        )}
        <Tooltip
          placement="bottom"
          // overlayClassName={styles.information}
          title={`Click to sort alerts by datetime in ${
            filters?.sortOrder === 'asc' ? 'desc' : 'asc'
          } order`}>
          <img
            src={IconSort}
            onClick={() =>
              onFilterChange({
                sortOrder: filters?.sortOrder === 'asc' ? 'desc' : 'asc'
              })
            }
            style={{
              transform:
                filters?.sortOrder === 'asc' ? 'scaleY(-1)' : 'scaleY(1)',
              cursor: 'pointer'
            }}
            alt=""
          />
        </Tooltip>
        <FilterDropdown filters={filters} placement="bottomRight">
          <StyledAlertListFilters
            filters={filters}
            onChange={onFilterChange}
            customerType={customerType}
          />
        </FilterDropdown>
        <StyledSearch
          allowClear
          placeholder="Enter Party Name/ID"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onSearch={onSearchButtonClick}
        />
        <div
          className="on-demand-screening-button-css"
          style={{ marginLeft: '16px' }}>
          <Dropdown
            overlay={takeActionMenu}
            disabled={selectedIds.length === 0}>
            <Button type="primary">
              <Space>
                Take Action
                <DownOutlined />
              </Space>
            </Button>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

AlertListFilters.propTypes = {
  dispatch: PropTypes.func.isRequired,
  customerType: PropTypes.array,
  selectedIds: PropTypes.array,
  selectedAlerts: PropTypes.array,
  filters: PropTypes.object,
  widgetConfig: PropTypes.array
};

export default AlertListFilters;
